import * as THREE from 'three';
import React, { useMemo } from 'react';
import CanvasButton from '../components/CanvasButton';
import { DisplaySpot } from '../../DisplaySpot';
import { Model3D } from '../components/Model3D';
import { MeshReflectorMaterial } from '@react-three/drei';

// Utility functions for room calculations and spot placement

const ensureNumber = (value, defaultValue = 0) => {
    return Number.isFinite(value) ? value : defaultValue;
};

// Default configuration values
const DEFAULT_CONFIG = {
    dimensions: {
        width: 35,
        depth: 25,
        height: 8,
        wallThickness: 0.2,
        doorWidth: 4
    },
    displayConfig: {
        margin: 4,
        spacing: 3,
        heightFromFloor: 3
    }
};

/**
 * Calculate the number of display spots that can fit on a wall
 */
export const calculateWallSpots = (wallLength, config) => {
    const margin = ensureNumber(config?.displayConfig?.margin, DEFAULT_CONFIG.displayConfig.margin);
    const spacing = ensureNumber(config?.displayConfig?.spacing, DEFAULT_CONFIG.displayConfig.spacing);
    return Math.floor((ensureNumber(wallLength) - margin * 2) / spacing);
};

/**
 * Calculate positions for wall display spots
 */
export const calculateWallSpotPositions = (config, isHorizontal, startX, startZ, count, rotation) => {
    const heightFromFloor = ensureNumber(config?.displayConfig?.heightFromFloor, DEFAULT_CONFIG.displayConfig.heightFromFloor);
    const spacing = ensureNumber(config?.displayConfig?.spacing, DEFAULT_CONFIG.displayConfig.spacing);
    const doorWidth = ensureNumber(config?.dimensions?.doorWidth, DEFAULT_CONFIG.dimensions.doorWidth);
    const roomWidth = ensureNumber(config?.dimensions?.width, DEFAULT_CONFIG.dimensions.width);

    return Array.from({ length: count }).map((_, index) => {
        const position = isHorizontal
            ? [
                ensureNumber(startX) + (index - Math.floor(count / 2)) * spacing,
                heightFromFloor,
                ensureNumber(startZ)
            ]
            : [
                ensureNumber(startX),
                heightFromFloor,
                ensureNumber(startZ) + (index - Math.floor(count / 2)) * spacing
            ];

        // Skip spots that would intersect with alcove divider walls
        if (isHorizontal) {
            if (Math.abs(position[0] - roomWidth / 6) < spacing / 2 ||
                Math.abs(position[0] + roomWidth / 6) < spacing / 2) {
                return null;
            }
        } else {
            if (Math.abs(position[2] - doorWidth / 2) < spacing / 2 ||
                Math.abs(position[2] + doorWidth / 2) < spacing / 2) {
                return null;
            }
        }

        return {
            position,
            rotation,
            id: `${isHorizontal ? 'h' : 'v'}-${startX}-${startZ}-${index}`
        };
    }).filter(Boolean); // Remove null entries
};

/**
 * Calculate positions for divider wall display spots
 */
export const calculateDividerSpotPositions = (config) => {
    const dimensions = {
        ...DEFAULT_CONFIG.dimensions,
        ...config?.dimensions
    };

    const displayConfig = {
        ...DEFAULT_CONFIG.displayConfig,
        ...config?.displayConfig
    };

    const { spacing, heightFromFloor } = displayConfig;
    const { width: dims_width, depth: dims_depth, doorWidth: dims_doorWidth } = dimensions;

    const spots = [];
    const dividerWallLength = (dims_depth - dims_doorWidth) / 2 - 1;
    const spotsPerDivider = Math.floor(dividerWallLength / spacing);

    // Helper to create spots for a section
    const createSectionSpots = (baseX, isBack) => {
        const z = isBack
            ? -dims_doorWidth / 2 - (dims_depth - dims_doorWidth) / 4
            : dims_doorWidth / 2 + (dims_depth - dims_doorWidth) / 4;

        return Array.from({ length: spotsPerDivider }).map((_, index) => {
            const spotZ = z + (index - Math.floor(spotsPerDivider / 2)) * spacing;
            return [
                // Left side of wall
                {
                    position: [baseX - 0.15, heightFromFloor, spotZ],
                    rotation: [0, -Math.PI / 2, 0],
                    id: `divider-${baseX < 0 ? 'left' : 'right'}-${isBack ? 'back' : 'front'}-left-${index}`
                },
                // Right side of wall
                {
                    position: [baseX + 0.15, heightFromFloor, spotZ],
                    rotation: [0, Math.PI / 2, 0],
                    id: `divider-${baseX < 0 ? 'left' : 'right'}-${isBack ? 'back' : 'front'}-right-${index}`
                }
            ];
        }).flat();
    };

    // Left divider walls
    const leftX = -dims_width / 6;
    spots.push(
        ...createSectionSpots(leftX, false), // Front section
        ...createSectionSpots(leftX, true)   // Back section
    );

    // Right divider walls
    const rightX = dims_width / 6;
    spots.push(
        ...createSectionSpots(rightX, false), // Front section
        ...createSectionSpots(rightX, true)   // Back section
    );

    return spots;
};

/**
 * Calculate all display spot positions for a room
 */
export const calculateAllSpotPositions = (config) => {
    const dimensions = {
        ...DEFAULT_CONFIG.dimensions,
        ...config?.dimensions
    };

    const { width, depth } = dimensions;

    // Calculate spots for main walls
    const backWallSpots = calculateWallSpots(width, config);
    const sideWallSpots = calculateWallSpots(depth, config);

    const spots = [
        // Back wall
        ...calculateWallSpotPositions(config, true, 0, -depth / 2 + 0.2, backWallSpots, [0, 0, 0]),
        // Front wall
        ...calculateWallSpotPositions(config, true, 0, depth / 2 - 0.2, backWallSpots, [0, Math.PI, 0]),
        // Left wall
        ...calculateWallSpotPositions(config, false, -width / 2 + 0.2, 0, sideWallSpots, [0, Math.PI / 2, 0]),
        // Right wall
        ...calculateWallSpotPositions(config, false, width / 2 - 0.2, 0, sideWallSpots, [0, -Math.PI / 2, 0]),
        // Divider walls
        ...calculateDividerSpotPositions(config)
    ];

    return spots;
};

export function createHexagonalRoomGeometry({
    roomConfig,
    createWallMaterial,
    onSpotSelect,
    canInteract,
    isShowMode,
    assets,
    selectedSpot,
    controlsRef,
    onAssetsUpdate,
    updateAssetTransform,
    galleryId,
    onHover
}) {
    // --- Geometry and material logic from HexagonalRoom.jsx ---
    const width = Number(roomConfig?.dimensions?.width) || 10;
    const height = Number(roomConfig?.dimensions?.height) || 5;
    const wallThickness = Number(roomConfig?.dimensions?.wallThickness) || 0.2;
    const radius = width / 2;

    // Calculate hexagon corners
    const hexagonCorners = Array.from({ length: 6 }).map((_, i) => {
        const angle = (Math.PI / 3) * i;
        return {
            x: radius * Math.cos(angle),
            z: radius * Math.sin(angle)
        };
    });

    // Create hexagon shape for floor/ceiling
    const hexagonShape = new THREE.Shape(hexagonCorners.map(c => new THREE.Vector2(c.x, c.z)));

    // Materials
    const floorMaterial = new THREE.MeshStandardMaterial({
        color: roomConfig?.materials?.floor?.color || '#ffffff',
        roughness: roomConfig?.materials?.floor?.roughness ?? 0.2,
        metalness: roomConfig?.materials?.floor?.metalness ?? 0.1,
        envMapIntensity: roomConfig?.materials?.floor?.envMapIntensity ?? 1.0
    });
    const ceilingMaterial = new THREE.MeshStandardMaterial({
        color: roomConfig?.materials?.ceiling?.color || '#ffffff',
        roughness: roomConfig?.materials?.ceiling?.roughness ?? 0.7,
        metalness: roomConfig?.materials?.ceiling?.metalness ?? 0,
        envMapIntensity: roomConfig?.materials?.ceiling?.envMapIntensity ?? 0.8
    });
    const wallMaterial = createWallMaterial ? createWallMaterial(roomConfig?.materials?.walls) : new THREE.MeshStandardMaterial({ color: '#f0f0f0', roughness: 0.85, metalness: 0 });

    // Floor/ceiling geometry
    const extrudeSettings = { depth: wallThickness, bevelEnabled: false };
    const floorGeometry = new THREE.ExtrudeGeometry(hexagonShape, extrudeSettings);

    // Walls
    const walls = [];
    for (let i = 0; i < 6; i++) {
        if (i === 0) {
            // Wall with large doorway
            const doorWidth = 5; // meters (much wider)
            const doorHeight = 4.0; // meters (much taller)
            const hallwayLength = 20.0; // meters (positive length)
            const direction = -1; // -1 for inward, 1 for outward
            // Get wall endpoints
            const cornerA = hexagonCorners[i];
            const cornerB = hexagonCorners[(i + 1) % 6];
            // Wall vector
            const wallVec = { x: cornerB.x - cornerA.x, z: cornerB.z - cornerA.z };
            const wallLength = Math.sqrt(wallVec.x * wallVec.x + wallVec.z * wallVec.z);
            // Center point of wall
            const centerX = (cornerA.x + cornerB.x) / 2;
            const centerZ = (cornerA.z + cornerB.z) / 2;
            // Unit direction vector
            const dirX = wallVec.x / wallLength;
            const dirZ = wallVec.z / wallLength;
            // Outward normal (points out from room)
            const normalX = -(cornerB.z - cornerA.z) / wallLength;
            const normalZ = (cornerB.x - cornerA.x) / wallLength;
            // Doorway segment endpoints
            const leftEndX = centerX - dirX * (doorWidth / 2);
            const leftEndZ = centerZ - dirZ * (doorWidth / 2);
            const rightStartX = centerX + dirX * (doorWidth / 2);
            const rightStartZ = centerZ + dirZ * (doorWidth / 2);
            // Helper to create a wall segment geometry
            function createWallSegmentGeometry(ax, az, bx, bz, y0, y1) {
                const geom = new THREE.BufferGeometry();
                const vertices = new Float32Array([
                    ax, y0, az,
                    bx, y0, bz,
                    bx, y1, bz,
                    ax, y1, az
                ]);
                const indices = [0, 1, 2, 0, 2, 3];
                const uvs = new Float32Array([0,0,1,0,1,1,0,1]);
                geom.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
                geom.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
                geom.setIndex(indices);
                geom.computeVertexNormals();
                return geom;
            }
            // Helper to create a rectangular plane geometry from four corners (for floor/ceiling)
            function createPlaneGeometry(a, b, c, d) {
                const geom = new THREE.BufferGeometry();
                const vertices = new Float32Array([
                    a.x, a.y, a.z,
                    b.x, b.y, b.z,
                    c.x, c.y, c.z,
                    d.x, d.y, d.z
                ]);
                const indices = [0, 1, 2, 0, 2, 3];
                const uvs = new Float32Array([0,0,1,0,1,1,0,1]);
                geom.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
                geom.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
                geom.setIndex(indices);
                geom.computeVertexNormals();
                return geom;
            }
            // Add left and right wall segments (room wall)
            walls.push({
                geometry: createWallSegmentGeometry(cornerA.x, cornerA.z, leftEndX, leftEndZ, 0, height),
                buttonPosition: [(cornerA.x + leftEndX) / 2, height/2, (cornerA.z + leftEndZ) / 2],
                buttonRotation: [0, Math.atan2(normalX, normalZ), 0],
                wallId: `hexagon_wall_0_left`,
                normal: [normalX, 0, normalZ]
            });
            walls.push({
                geometry: createWallSegmentGeometry(rightStartX, rightStartZ, cornerB.x, cornerB.z, 0, height),
                buttonPosition: [(rightStartX + cornerB.x) / 2, height/2, (rightStartZ + cornerB.z) / 2],
                buttonRotation: [0, Math.atan2(normalX, normalZ), 0],
                wallId: `hexagon_wall_0_right`,
                normal: [normalX, 0, normalZ]
            });
            // Top segment (lintel) above the door
            if (doorHeight < height) {
                walls.push({
                    geometry: createWallSegmentGeometry(leftEndX, leftEndZ, rightStartX, rightStartZ, doorHeight, height),
                    buttonPosition: [(leftEndX + rightStartX) / 2, (doorHeight + height) / 2, (leftEndZ + rightStartZ) / 2],
                    buttonRotation: [0, Math.atan2(normalX, normalZ), 0],
                    wallId: `hexagon_wall_0_top`,
                    normal: [normalX, 0, normalZ]
                });
            }
            // --- Hallway geometry ---
            // Calculate hallway corners (extrude in direction)
            const outX = normalX * direction;
            const outZ = normalZ * direction;
            // Four corners of the door opening
            const doorLeftBottom = { x: leftEndX, y: 0, z: leftEndZ };
            const doorLeftTop = { x: leftEndX, y: doorHeight, z: leftEndZ };
            const doorRightBottom = { x: rightStartX, y: 0, z: rightStartZ };
            const doorRightTop = { x: rightStartX, y: doorHeight, z: rightStartZ };
            // Four corners at the end of the hallway
            const hallLeftBottom = { x: leftEndX + outX * hallwayLength, y: 0, z: leftEndZ + outZ * hallwayLength };
            const hallLeftTop = { x: leftEndX + outX * hallwayLength, y: doorHeight, z: leftEndZ + outZ * hallwayLength };
            const hallRightBottom = { x: rightStartX + outX * hallwayLength, y: 0, z: rightStartZ + outZ * hallwayLength };
            const hallRightTop = { x: rightStartX + outX * hallwayLength, y: doorHeight, z: rightStartZ + outZ * hallwayLength };
            // Hallway left wall
            walls.push({
                geometry: createWallSegmentGeometry(doorLeftBottom.x, doorLeftBottom.z, hallLeftBottom.x, hallLeftBottom.z, 0, doorHeight),
                buttonPosition: [(doorLeftBottom.x + hallLeftBottom.x) / 2, doorHeight/2, (doorLeftBottom.z + hallLeftBottom.z) / 2],
                buttonRotation: [0, Math.atan2(outX, outZ), 0],
                wallId: `hexagon_hallway_left`,
                normal: [outX, 0, outZ]
            });
            // Hallway right wall
            walls.push({
                geometry: createWallSegmentGeometry(hallRightBottom.x, hallRightBottom.z, doorRightBottom.x, doorRightBottom.z, 0, doorHeight),
                buttonPosition: [(hallRightBottom.x + doorRightBottom.x) / 2, doorHeight/2, (hallRightBottom.z + doorRightBottom.z) / 2],
                buttonRotation: [0, Math.atan2(outX, outZ), 0],
                wallId: `hexagon_hallway_right`,
                normal: [outX, 0, outZ]
            });
            // Hallway ceiling (proper plane)
            walls.push({
                geometry: createPlaneGeometry(doorLeftTop, hallLeftTop, hallRightTop, doorRightTop),
                buttonPosition: [(doorLeftTop.x + hallRightTop.x) / 2, doorHeight, (doorLeftTop.z + hallRightTop.z) / 2],
                buttonRotation: [0, Math.atan2(outX, outZ), 0],
                wallId: `hexagon_hallway_ceiling`,
                normal: [0, 1, 0]
            });
            // Hallway floor (proper plane)
            walls.push({
                geometry: createPlaneGeometry(doorLeftBottom, doorRightBottom, hallRightBottom, hallLeftBottom),
                buttonPosition: [(doorLeftBottom.x + hallRightBottom.x) / 2, 0, (doorLeftBottom.z + hallRightBottom.z) / 2],
                buttonRotation: [0, Math.atan2(outX, outZ), 0],
                wallId: `hexagon_hallway_floor`,
                normal: [0, 1, 0]
            });
            // --- Entry Room ---
            // Entry room parameters
            const entryWidth = 10.0; // meters (wider than hallway)
            const entryDepth = 8.0; // meters
            const entryHeight = 7; // meters
            // The entry room starts at the end of the hallway
            // We'll use the same direction as the hallway
            // Calculate the four corners of the entry room floor
            // Hallway end (door opening at end of hallway)
            const entryOriginLeft = { x: hallLeftBottom.x, y: 0, z: hallLeftBottom.z };
            const entryOriginRight = { x: hallRightBottom.x, y: 0, z: hallRightBottom.z };
            // Direction vector (same as hallway)
            const entryDirX = outX;
            const entryDirZ = outZ;
            // Perpendicular vector (to get room width)
            const perpX = -(entryDirZ);
            const perpZ = entryDirX;
            // Center the hallway on the entry room front wall
            // Calculate the center point at the end of the hallway
            const hallwayCenter = {
                x: (entryOriginLeft.x + entryOriginRight.x) / 2,
                y: 0,
                z: (entryOriginLeft.z + entryOriginRight.z) / 2
            };
            // Calculate the left and right corners of the entry room front wall
            const entryFrontLeft = {
                x: hallwayCenter.x - perpX * (entryWidth / 2),
                y: 0,
                z: hallwayCenter.z - perpZ * (entryWidth / 2)
            };
            const entryFrontRight = {
                x: hallwayCenter.x + perpX * (entryWidth / 2),
                y: 0,
                z: hallwayCenter.z + perpZ * (entryWidth / 2)
            };
            // Far corners
            const entryFarLeft = {
                x: entryFrontLeft.x + entryDirX * entryDepth,
                y: 0,
                z: entryFrontLeft.z + entryDirZ * entryDepth
            };
            const entryFarRight = {
                x: entryFrontRight.x + entryDirX * entryDepth,
                y: 0,
                z: entryFrontRight.z + entryDirZ * entryDepth
            };
            
            // DEBUG: Log entry room coordinates
            console.log('🏠 ENTRY ROOM DEBUG:');
            console.log('Entry room center (front):', {
                x: (entryFrontLeft.x + entryFrontRight.x) / 2,
                z: (entryFrontLeft.z + entryFrontRight.z) / 2
            });
            console.log('Entry room center (back):', {
                x: (entryFarLeft.x + entryFarRight.x) / 2,
                z: (entryFarLeft.z + entryFarRight.z) / 2
            });
            console.log('Entry direction:', { entryDirX, entryDirZ });
            console.log('Hallway center:', hallwayCenter);
            // Top corners (for ceiling)
            const entryFrontLeftTop = { ...entryFrontLeft, y: entryHeight };
            const entryFrontRightTop = { ...entryFrontRight, y: entryHeight };
            const entryFarLeftTop = { ...entryFarLeft, y: entryHeight };
            const entryFarRightTop = { ...entryFarRight, y: entryHeight };
            // Floor
            walls.push({
                geometry: createPlaneGeometry(entryFrontLeft, entryFrontRight, entryFarRight, entryFarLeft),
                buttonPosition: [(entryFrontLeft.x + entryFarRight.x) / 2, 0, (entryFrontLeft.z + entryFarRight.z) / 2],
                buttonRotation: [0, Math.atan2(entryDirX, entryDirZ), 0],
                wallId: `entry_room_floor`,
                normal: [0, 1, 0]
            });
            // Ceiling
            walls.push({
                geometry: createPlaneGeometry(entryFrontLeftTop, entryFarLeftTop, entryFarRightTop, entryFrontRightTop),
                buttonPosition: [(entryFrontLeftTop.x + entryFarRightTop.x) / 2, entryHeight, (entryFrontLeftTop.z + entryFarRightTop.z) / 2],
                buttonRotation: [0, Math.atan2(entryDirX, entryDirZ), 0],
                wallId: `entry_room_ceiling`,
                normal: [0, -1, 0]
            });
            // Left wall
            walls.push({
                geometry: createWallSegmentGeometry(entryFrontLeft.x, entryFrontLeft.z, entryFarLeft.x, entryFarLeft.z, 0, entryHeight),
                buttonPosition: [(entryFrontLeft.x + entryFarLeft.x) / 2, entryHeight/2, (entryFrontLeft.z + entryFarLeft.z) / 2],
                buttonRotation: [0, Math.atan2(perpX, perpZ), 0],
                wallId: `entry_room_left`,
                normal: [perpX, 0, perpZ]
            });
            // Right wall
            walls.push({
                geometry: createWallSegmentGeometry(entryFarRight.x, entryFarRight.z, entryFrontRight.x, entryFrontRight.z, 0, entryHeight),
                buttonPosition: [(entryFarRight.x + entryFrontRight.x) / 2, entryHeight/2, (entryFarRight.z + entryFrontRight.z) / 2],
                buttonRotation: [0, Math.atan2(-perpX, -perpZ), 0],
                wallId: `entry_room_right`,
                normal: [-perpX, 0, -perpZ]
            });
            // Back wall (opposite hallway)
            walls.push({
                geometry: createWallSegmentGeometry(entryFarLeft.x, entryFarLeft.z, entryFarRight.x, entryFarRight.z, 0, entryHeight),
                buttonPosition: [(entryFarLeft.x + entryFarRight.x) / 2, entryHeight/2, (entryFarLeft.z + entryFarRight.z) / 2],
                buttonRotation: [0, Math.atan2(entryDirX, entryDirZ), 0],
                wallId: `entry_room_back`,
                normal: [entryDirX, 0, entryDirZ]
            });
            // --- Connector walls ---
            // Left connector (from hallway left edge to entry room front left)
            walls.push({
                geometry: createWallSegmentGeometry(entryOriginLeft.x, entryOriginLeft.z, entryFrontLeft.x, entryFrontLeft.z, 0, Math.min(doorHeight, entryHeight)),
                buttonPosition: [(entryOriginLeft.x + entryFrontLeft.x) / 2, Math.min(doorHeight, entryHeight)/2, (entryOriginLeft.z + entryFrontLeft.z) / 2],
                buttonRotation: [0, Math.atan2(perpX, perpZ), 0],
                wallId: `entry_connector_left`,
                normal: [perpX, 0, perpZ]
            });
            // Right connector (from hallway right edge to entry room front right)
            walls.push({
                geometry: createWallSegmentGeometry(entryFrontRight.x, entryFrontRight.z, entryOriginRight.x, entryOriginRight.z, 0, Math.min(doorHeight, entryHeight)),
                buttonPosition: [(entryFrontRight.x + entryOriginRight.x) / 2, Math.min(doorHeight, entryHeight)/2, (entryFrontRight.z + entryOriginRight.z) / 2],
                buttonRotation: [0, Math.atan2(-perpX, -perpZ), 0],
                wallId: `entry_connector_right`,
                normal: [-perpX, 0, -perpZ]
            });
            // --- Transom wall segment above hallway (to close gap between hallway and entry room ceiling) ---
            if (entryHeight > doorHeight) {
                // Create transom walls only on the SIDES of the doorway, not blocking the center
                // Left transom (left edge to left side of door)
                const leftTransomLeft = { x: entryFrontLeft.x, y: doorHeight, z: entryFrontLeft.z };
                const leftTransomRight = { x: doorLeftBottom.x, y: doorHeight, z: doorLeftBottom.z };
                const leftTransomTopLeft = { x: entryFrontLeft.x, y: entryHeight, z: entryFrontLeft.z };
                const leftTransomTopRight = { x: doorLeftBottom.x, y: entryHeight, z: doorLeftBottom.z };
                
                if (Math.abs(leftTransomLeft.x - leftTransomRight.x) > 0.1 || Math.abs(leftTransomLeft.z - leftTransomRight.z) > 0.1) {
                    walls.push({
                        geometry: createPlaneGeometry(leftTransomLeft, leftTransomRight, leftTransomTopRight, leftTransomTopLeft),
                        buttonPosition: [
                            (leftTransomLeft.x + leftTransomTopRight.x) / 2,
                            (doorHeight + entryHeight) / 2,
                            (leftTransomLeft.z + leftTransomTopRight.z) / 2
                        ],
                        buttonRotation: [0, Math.atan2(entryDirX, entryDirZ), 0],
                        wallId: `entry_transom_left`,
                        normal: [entryDirX, 0, entryDirZ]
                    });
                }
                
                // Right transom (right side of door to right edge)
                const rightTransomLeft = { x: doorRightBottom.x, y: doorHeight, z: doorRightBottom.z };
                const rightTransomRight = { x: entryFrontRight.x, y: doorHeight, z: entryFrontRight.z };
                const rightTransomTopLeft = { x: doorRightBottom.x, y: entryHeight, z: doorRightBottom.z };
                const rightTransomTopRight = { x: entryFrontRight.x, y: entryHeight, z: entryFrontRight.z };
                
                if (Math.abs(rightTransomLeft.x - rightTransomRight.x) > 0.1 || Math.abs(rightTransomLeft.z - rightTransomRight.z) > 0.1) {
                    walls.push({
                        geometry: createPlaneGeometry(rightTransomLeft, rightTransomRight, rightTransomTopRight, rightTransomTopLeft),
                        buttonPosition: [
                            (rightTransomLeft.x + rightTransomTopRight.x) / 2,
                            (doorHeight + entryHeight) / 2,
                            (rightTransomLeft.z + rightTransomTopRight.z) / 2
                        ],
                        buttonRotation: [0, Math.atan2(entryDirX, entryDirZ), 0],
                        wallId: `entry_transom_right`,
                        normal: [entryDirX, 0, entryDirZ]
                    });
                }
                
                // Wide transom spanning full entry room width above doorway
                const wideTransomBottomLeft = { x: entryFrontLeft.x, y: doorHeight, z: entryFrontLeft.z };
                const wideTransomBottomRight = { x: entryFrontRight.x, y: doorHeight, z: entryFrontRight.z };
                const wideTransomTopLeft = { x: entryFrontLeft.x, y: entryHeight, z: entryFrontLeft.z };
                const wideTransomTopRight = { x: entryFrontRight.x, y: entryHeight, z: entryFrontRight.z };
                
                walls.push({
                    geometry: createPlaneGeometry(wideTransomBottomLeft, wideTransomTopLeft, wideTransomTopRight, wideTransomBottomRight),
                    buttonPosition: [
                        (wideTransomBottomLeft.x + wideTransomTopRight.x) / 2,
                        (doorHeight + entryHeight) / 2,
                        (wideTransomBottomLeft.z + wideTransomTopRight.z) / 2
                    ],
                    buttonRotation: [0, Math.atan2(entryDirX, entryDirZ), 0],
                    wallId: `entry_transom_wide`,
                    normal: [-entryDirX, 0, -entryDirZ]
                });
            }
            // (No front wall, so hallway opens directly into entry room)
            continue;
        }
        const cornerA = hexagonCorners[i];
        const cornerB = hexagonCorners[(i + 1) % 6];
        const wallGeometry = new THREE.BufferGeometry();
        const vertices = new Float32Array([
            cornerA.x, 0, cornerA.z,
            cornerB.x, 0, cornerB.z,
            cornerB.x, height, cornerB.z,
            cornerA.x, height, cornerA.z
        ]);
        const indices = [0, 1, 2, 0, 2, 3];
        const uvs = new Float32Array([0,0,1,0,1,1,0,1]);
        wallGeometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
        wallGeometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
        wallGeometry.setIndex(indices);
        wallGeometry.computeVertexNormals();
        // Wall normal
        const normalX = -(cornerB.z - cornerA.z);
        const normalZ = cornerB.x - cornerA.x;
        const length = Math.sqrt(normalX * normalX + normalZ * normalZ);
        const normalizedX = normalX / length;
        const normalizedZ = normalZ / length;
        const wallNormal = [normalizedX, 0, normalizedZ];
        // Button position/rotation
        const centerX = (cornerA.x + cornerB.x) / 2;
        const centerZ = (cornerA.z + cornerB.z) / 2;
        const buttonOffset = 0.1;
        const buttonX = centerX + normalizedX * buttonOffset;
        const buttonZ = centerZ + normalizedZ * buttonOffset;
        walls.push({
            geometry: wallGeometry,
            buttonPosition: [buttonX, height/2, buttonZ],
            buttonRotation: [0, Math.atan2(normalX, normalZ), 0],
            wallId: `hexagon_wall_${i}`,
            normal: wallNormal
        });
    }

    // Asset filtering
    const models = assets ? Object.entries(assets).filter(([id, asset]) => asset?.type === 'model' || (asset?.type === 'nft' && asset?.isModel)) : [];
    const wallArt = assets ? Object.entries(assets).filter(([id, asset]) => asset && !asset.isModel && (asset.type === 'image' || asset.type === 'nft')) : [];

    // Helper for transforms
    const ensureValidArray = (arr, def) => Array.isArray(arr) ? arr.map(v => Number(v) || 0) : def;
    const ensureSafeVector3 = (x=0,y=0,z=0) => [Number(x)||0,Number(y)||0,Number(z)||0];

    // Spot add handler
    function handleAddSpot(spotConfig) {
        const newSpotId = `spot-${spotConfig.wallId}-${Date.now()}`;
        const emptyAsset = {
            type: 'image',
            url: '',
            position: ensureSafeVector3(...spotConfig.position),
            rotation: ensureSafeVector3(...spotConfig.rotation),
            scale: [1,1,1],
            surfaceNormal: spotConfig.surfaceNormal,
            width: 2,
            height: 2,
            depth: 0.1
        };
        if (onAssetsUpdate) {
            const updatedAssets = { ...assets, [newSpotId]: emptyAsset };
            onAssetsUpdate(updatedAssets);
            if (onSpotSelect) onSpotSelect(newSpotId);
        }
    }
    // Transform handler
    async function handleTransformEnd(spotId, transform) {
        const existingAsset = assets?.[spotId];
        if (!existingAsset) return;
        const updatedAsset = {
            ...existingAsset,
            position: ensureValidArray(transform.position, existingAsset.position),
            rotation: ensureValidArray(transform.rotation, existingAsset.rotation),
            scale: ensureValidArray(transform.scale, existingAsset.scale)
        };
        if (typeof updateAssetTransform === 'function') {
            await updateAssetTransform(spotId, updatedAsset);
        } else if (typeof onAssetsUpdate === 'function') {
            const updatedAssets = { ...assets, [spotId]: updatedAsset };
            onAssetsUpdate(updatedAssets);
        }
    }
    // Transform getter
    function getEffectiveTransform(spotId, asset) {
        if (asset?.position || asset?.rotation || asset?.scale) {
            return {
                position: ensureValidArray(asset.position, [0,0,0]),
                rotation: ensureValidArray(asset.rotation, [0,0,0]),
                scale: ensureValidArray(asset.scale, [1,1,1])
            };
        }
        return { position: [0,0,0], rotation: [0,0,0], scale: [1,1,1] };
    }

    // Return React elements for Room.jsx to render
    return {
        floor: <>
            <mesh geometry={floorGeometry} material={floorMaterial} position={[0,0,0]} rotation={[Math.PI/2,0,0]} />
            <mesh position={[0,0.005,0]} rotation={[-Math.PI/2,0,0]} renderOrder={-1} visible={true}>
                <planeGeometry args={[width * 3, width * 3]} />
                <MeshReflectorMaterial
                    blur={[400, 100]}
                    resolution={256}
                    mixBlur={0}
                    mixStrength={0.6}
                    roughness={0.1}
                    depthScale={0}
                    minDepthThreshold={0.9}
                    maxDepthThreshold={1}
                    color="#000000"
                    metalness={0}
                    mirror={0.5}
                    transparent={true}
                    opacity={0.15}
                    depthWrite={false}
                />
            </mesh>
        </>,
        ceiling: <mesh geometry={floorGeometry} material={ceilingMaterial} position={[0,height,0]} rotation={[-Math.PI/2,0,0]} />,
        walls: walls.map((wall, idx) => {
            // Create individual wall material for each wall (same as HexagonalRoom.jsx)
            const individualWallMaterial = createWallMaterial ? createWallMaterial(roomConfig?.materials?.walls) : 
                new THREE.MeshStandardMaterial({ color: '#f0f0f0', roughness: 0.85, metalness: 0, side: THREE.DoubleSide });
            
            return (
                <mesh key={`wall-${idx}`} geometry={wall.geometry}>
                    <primitive object={individualWallMaterial} />
                </mesh>
            );
        }),
        buttons: !isShowMode ? walls.filter(wall => {
            // Remove buttons from floor and ceiling
            if (wall.wallId.includes('floor') || wall.wallId.includes('ceiling')) return false;
            return true;
        }).map((wall, idx) => {
            let rotation = wall.buttonRotation ? [...wall.buttonRotation] : [0, 0, 0];
            // Entry room back wall: face inward
            if (wall.wallId === 'entry_room_back') {
                rotation[1] = Math.atan2(-wall.normal[0], -wall.normal[2]);
            }
            // Entry connector transoms: rotate 90° to face inward
            if (wall.wallId.includes('connector')) {
                rotation[1] += Math.PI / 2;
            }
            // Hallway left/right walls: rotate 90° to face inward
            if (wall.wallId.includes('hallway_left') || wall.wallId.includes('hallway_right')) {
                rotation[1] += Math.PI / 2;
            }
            // If left hallway wall or right connector, rotate 180° more
            if (wall.wallId.includes('hallway_left') || wall.wallId.includes('connector_right')) {
                rotation[1] += Math.PI;
            }
            return (
                <CanvasButton
                    key={`canvas-${idx}`}
                    wallId={wall.wallId}
                    position={wall.buttonPosition}
                    rotation={rotation}
                    onAddSpot={spotConfig => handleAddSpot({ ...spotConfig, surfaceNormal: wall.normal })}
                    isShowMode={isShowMode}
                />
            );
        }) : null,
        wallArt: wallArt.map(([spotId, asset]) => {
            const transform = getEffectiveTransform(spotId, asset);
            return (
                <DisplaySpot
                    key={spotId}
                    id={spotId}
                    position={ensureSafeVector3(...transform.position)}
                    rotation={ensureSafeVector3(...transform.rotation)}
                    scale={ensureSafeVector3(...transform.scale)}
                    surfaceNormal={asset?.surfaceNormal}
                    asset={asset || { type: 'image' }}
                    isSelected={selectedSpot === spotId}
                    canInteract={!isShowMode}
                    onSelect={onSpotSelect}
                    controlsRef={controlsRef}
                    onTransformEnd={transform => handleTransformEnd(spotId, transform)}
                    isShowMode={isShowMode}
                />
            );
        }),
        models: models.map(([id, asset]) => {
            const transform = getEffectiveTransform(id, asset);
            return (
                <Model3D
                    key={id}
                    id={id}
                    position={ensureSafeVector3(...transform.position)}
                    rotation={ensureSafeVector3(...transform.rotation)}
                    scale={ensureSafeVector3(...transform.scale)}
                    asset={asset}
                    selected={selectedSpot === id}
                    canInteract={!isShowMode}
                    onSelect={onSpotSelect}
                    controlsRef={controlsRef}
                    onTransformEnd={transform => handleTransformEnd(id, transform)}
                    isShowMode={isShowMode}
                    onHover={onHover}
                />
            );
        })
    };
} 